import {
    notifyManager,
    QueryClient,
    QueryObserver,
    skipToken,
    type QueryKey,
    type QueryObserverOptions,
} from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { useServiceContext } from "../../contexts/services/hooks/use-services-context";
import type { SdkServices } from "../../contexts/services/types/services";

export type LiveSnapshot<T> = Readonly<{
    data: T | undefined;
    isLoading: boolean;
    error: Error | undefined;
}>;

export type LiveState<T> = LiveSnapshot<T> & {
    /** Recharge la valeur depuis le SDK ; sans effet tant qu'aucune session n'existe. */
    refresh: () => Promise<void>;
};

const NO_SESSION_CLIENT = new QueryClient();

/**
 * Fait de la session lu dans son cache de requêtes ; en chargement tant qu'aucune session
 * n'existe. Sans `load`, le fait n'est alimenté que par le SDK.
 */
export function useSessionQuery<TData, TSelected = TData>(
    queryKey: QueryKey,
    load?: (sdk: SdkServices) => Promise<TData>,
    select?: (data: TData) => TSelected,
): LiveState<TSelected> {
    const { sdk } = useServiceContext();
    const canLoad = !!sdk && !!load;
    const client = sdk?.queryClient ?? NO_SESSION_CLIENT;
    const options: QueryObserverOptions<
        TData,
        Error,
        TSelected,
        TData,
        QueryKey
    > = {
        queryKey,
        queryFn: canLoad ? () => load(sdk) : skipToken,
        select,
        retry: false,
    };

    // useQuery garde le client de son premier rendu : l'observer est recréé à chaque session
    const observer = useMemo(
        () => new QueryObserver(client, options),
        [client],
    );
    useEffect(() => observer.setOptions(options));

    const { data, isPending, error, refetch } = useSyncExternalStore(
        useCallback(
            (onChange: () => void) =>
                observer.subscribe(notifyManager.batchCalls(onChange)),
            [observer],
        ),
        () => observer.getCurrentResult(),
    );

    const refresh = useCallback(async () => {
        if (canLoad) await refetch();
    }, [canLoad, refetch]);

    return useMemo(
        () => ({
            data,
            isLoading: isPending,
            error: error ?? undefined,
            refresh,
        }),
        [data, isPending, error, refresh],
    );
}
